// Library methods
import { useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { ThemeProvider, createTheme, useMediaQuery } from "@mui/material";

// MUI Components
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";

// Assets
import eyeLogo from "../../../../../assets/images/logos/eyelogo.png";

// Utilities
import useClinic from "../../../../../hooks/useClinic";
import { ValidatePDFContext } from "../../../../../contexts/ValidatePDFContext";
import {
  ReportInfoHeaderContent,
  ReportInfoShared,
} from "../../../../../components/UI/ReportInfoShared";
import { ReportEachSectionContext } from "../../../../../contexts/CommentContext";
import { eyeTypes, examCardEnums } from "../../../../../enums";
import { getLocalDateTimeWithZone } from "../../../../../utils/dateHelper";
import { getTutorialCompletionText } from "../../../../../utils/examReportsHelper";

const ReportInfo = () => {
  // internationalization
  const { t } = useTranslation();
  const { clinicTimezone, clinicSettings } = useClinic();
  const { forBackendPdf } = useContext(ValidatePDFContext);
  const { eyeRight, eyeBoth } = eyeTypes;

  const {
    examReport,
    eyeReport,
    duration,
    sectionId: firstSectionId,
    sectionId2,
  } = useContext(ReportEachSectionContext);

  const theme = createTheme({
    typography: {
      allVariants: {
        fontFamily: ["Arial", "Verdana", "sans-serif"],
      },
    },
  });

  const isWiderThanSmallScreen = useMediaQuery(theme.breakpoints.up("sm"));

  const localDateAndTimeWithZone = useMemo(
    () =>
      examReport?.timing?.start
        ? getLocalDateTimeWithZone(examReport.timing.start, clinicTimezone)
        : null,
    [clinicTimezone, examReport?.timing?.start]
  );

  const gridSizeData = useMemo(
    () => [
      { sm: 4, md: 4, lg: 4, xl: 4 },
      {
        sm: 3,
        md: {
          sm: 3,
          md:
            forBackendPdf &&
            clinicSettings?.monocularReportPdfLayoutOption === "Single" &&
            firstSectionId != null &&
            sectionId2 != null
              ? 3
              : 2.4,
          lg: 3,
          xl: 2.4,
        },
      },
    ],
    [
      clinicSettings?.monocularReportPdfLayoutOption,
      firstSectionId,
      forBackendPdf,
      sectionId2,
    ]
  );

  const eyeAbbreviation = useMemo(
    () => (eyeReport === eyeBoth ? "OU" : eyeReport === eyeRight ? "OD" : "OS"),
    [eyeReport, eyeBoth, eyeRight]
  );

  const infoData = useMemo(
    () => [
      [
        {
          title: t("word_eye"),
          body:
            eyeReport === eyeRight
              ? t("word_right")
              : eyeReport === eyeBoth
                ? t("word_binocular")
                : t("word_left"),
        },
        { title: t("word_saturation"), body: t("word_saturated") },
      ],
      [
        {
          title: t("report_exam_date"),
          body: localDateAndTimeWithZone?.date ?? t("word_na"),
          isBlack: true,
        },
        {
          title: t("report_exam_time"),
          body: localDateAndTimeWithZone?.time ?? t("word_na"),
          isBlack: true,
        },
        { title: t("report_exam_duration"), body: duration },
        getTutorialCompletionText(
          examReport?.postProcessing?.tutorialCompletionState,
          t
        ) && {
          title: t("word_tutorial"),
          body: getTutorialCompletionText(
            examReport?.postProcessing?.tutorialCompletionState,
            t
          ),
          isBlack: true,
        },
      ],
    ],
    [
      duration,
      eyeReport,
      localDateAndTimeWithZone?.date,
      localDateAndTimeWithZone?.time,
      examReport?.postProcessing?.tutorialCompletionState,
      eyeRight,
      eyeBoth,
      t,
    ]
  );
  return (
    <ThemeProvider theme={theme}>
      <Grid
        container
        columnSpacing={2}
        my={1}
        sx={{ display: "flex", alignItems: "start" }}
      >
        <Grid container item xs={12}>
          {forBackendPdf && (
            <Grid
              item
              xs={1}
              style={{
                alignItems: "center",
              }}
            >
              <img
                src={eyeLogo}
                alt="eye logo"
                style={{ width: "100%", height: "auto" }}
              />
            </Grid>
          )}
          <Grid container item xs={forBackendPdf ? 11 : 12} mb={1.5}>
            <Grid
              item
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              border={1}
              xs={12}
            >
              <Grid
                item
                sx={{
                  display: "flex",
                  background: "black",
                  width: 70,
                  height: "100%",
                  color: "white",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                xs={1.5}
                sm={1}
              >
                <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                  {eyeAbbreviation}
                </Typography>
              </Grid>
              <ReportInfoHeaderContent
                isWiderThanSmallScreen={isWiderThanSmallScreen}
                examTypeTranslation={`${t(examCardEnums.COLOR_VISION.label)} - `}
              />
            </Grid>
          </Grid>
        </Grid>
        {/* color vision */}
        <Grid container item display="flex" justifyContent={"space-between"}>
          <ReportInfoShared data={infoData} gridSizeData={gridSizeData} />
        </Grid>
      </Grid>
    </ThemeProvider>
  );
};

export default ReportInfo;
